import useShowTooltip from 'hooks/useShowTooltip';
import { Line } from 'react-chartjs-2';
import { Card, CardHeader, CardBody } from 'reactstrap';
import moment from 'moment';
import { removeHtmlTags } from 'helpers/global';
import { textEllipsis } from 'helpers/global';
import useWhiteLabel from 'hooks/useWhiteLabel';

var colors = {
  gray: {
    100: '#f6f9fc',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#8898aa',
    700: '#525f7f',
    800: '#32325d',
    900: '#212529'
  },
  theme: {
    default: '#172b4d',
    primary: '#5e72e4',
    secondary: '#f4f5f7',
    info: '#11cdef',
    success: '#2dce89',
    danger: '#f5365c',
    warning: '#fb6340',
    brand: '#fe5900'
  },
  black: '#12263F',
  white: '#FFFFFF',
  transparent: 'transparent'
};

const GivGraph = ({ dateFrom, dateUntil, isLoading, data: lineGraphData, eyebrow }) => {
  const { isWhiteLabelTeam } = useWhiteLabel();

  const localEyebro = eyebrow || (isWhiteLabelTeam ? 'Index Value Graph' : 'GIV Graph');

  const chartSurveyData = lineGraphData
    ? lineGraphData.data.map(({ label, count }) => ({ label, count }))
    : [];

  const firstDateIndex = lineGraphData?.data.findIndex((elm) => elm.label === dateFrom);
  if (firstDateIndex < 0) {
    chartSurveyData.unshift({
      label: dateFrom,
      count: 0
    });
  }

  const lastDateIndex = lineGraphData?.data.findIndex((elm) => elm.label === dateUntil);
  if (lastDateIndex < 0) {
    chartSurveyData.push({
      label: dateUntil,
      count: 0
    });
  }

  useShowTooltip();

  const chartSurveyCompleted = {
    options: {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 16
        }
      },
      elements: {
        point: {
          radius: 6,
          backgroundColor: colors.theme['brand']
        },
        line: {
          tension: 0.4,
          borderWidth: 4,
          borderColor: colors.theme['brand'],
          backgroundColor: colors.transparent,
          borderCapStyle: 'rounded'
        }
      },
      scales: {
        yAxes: [
          {
            gridLines: {
              color: colors.gray[200],
              zeroLineColor: colors.gray[200]
            },
            ticks: {
              stepSize: 0.5,
              beginAtZero: true,
              precision: 0
            }
          }
        ]
      },
      layout: {
        padding: {
          top: 5
        }
      }
    },
    data: {
      labels: chartSurveyData.map((elm) => moment(elm.label).format('DD/MM/YYYY')),
      datasets: [
        {
          label: isWhiteLabelTeam ? 'IV Value' : 'GIV value',
          data: chartSurveyData.map((elm) => elm.count)
        }
      ]
    }
  };

  return (
    <Card className="no-split">
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="surtitle text-black">{localEyebro}</h6>
            <h5 className="h3 mb-0 text-brand result-survey-question-text">
              {textEllipsis(removeHtmlTags(lineGraphData?.name), 40)}
            </h5>
          </div>
          {lineGraphData?.avg && (
            <div className="text-sm font-weight-bold">Average Value: {lineGraphData?.avg}</div>
          )}
        </div>
      </CardHeader>
      <CardBody style={{ height: '398px' }}>
        {isLoading && (
          <div className="h-100 d-flex flex-column justify-content-center">
            <p className="text-center mb-0">Loading...</p>
          </div>
        )}
        {!isLoading && (
          <div className="chart">
            <Line
              data={chartSurveyCompleted.data}
              options={chartSurveyCompleted.options}
              id="chart-survey-completed"
              className="chart-canvas"
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default GivGraph;
